import { AlertTriangle } from "lucide-react";

import { formatToBRL } from "@/utils/formatToMoney";
import { IDemonstrativeMovement } from "src/@types/store-conciliation";

import TransferList from "./transfer-list";
import {
  DEMONSTRATIVE_CLASSES,
  DEMONSTRATIVE_GROUPS,
  DEMONSTRATIVE_LABELS,
  DEMONSTRATIVE_TEST_IDS,
  DemonstrativeMovementsProps,
  GroupDivergence,
  rowTestId,
} from "./types";
import { divergenceOf } from "./utils";

const EMPTY_MOVEMENT: IDemonstrativeMovement = { amount: 0, quantity: 0 };

export default function DemonstrativeMovements({
  demonstrative,
}: DemonstrativeMovementsProps) {
  if (!demonstrative) {
    return (
      <div className="border w-full rounded p-3">
        <p className="text-sm text-slate-500">
          {DEMONSTRATIVE_LABELS.unavailable}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      {DEMONSTRATIVE_GROUPS.map((group) => {
        const movements: IDemonstrativeMovement[] = group.slots.map(
          ({ slot }) => demonstrative.movements?.[slot] ?? EMPTY_MOVEMENT,
        );
        const divergence: GroupDivergence = divergenceOf(group, movements);
        const rowDivergent: boolean = divergence.amount || divergence.quantity;

        return (
          <div
            key={group.title}
            data-testid={DEMONSTRATIVE_TEST_IDS.group}
            className="border w-full rounded overflow-hidden"
          >
            <div className="px-3 py-2 bg-gray-50 border-b">
              <p className="font-semibold text-sm">{group.title}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-300">
              {group.slots.map(({ slot, label }, index) => {
                const movement: IDemonstrativeMovement = movements[index];

                return (
                  <div
                    key={slot}
                    data-testid={rowTestId(slot)}
                    className="w-full p-2 sm:p-3 flex justify-between items-start gap-2"
                  >
                    <p
                      data-testid={DEMONSTRATIVE_TEST_IDS.label}
                      data-divergent={rowDivergent}
                      className={
                        rowDivergent
                          ? DEMONSTRATIVE_CLASSES.divergentLabel
                          : DEMONSTRATIVE_CLASSES.label
                      }
                    >
                      {rowDivergent && (
                        <AlertTriangle
                          className={DEMONSTRATIVE_CLASSES.icon}
                          aria-label={DEMONSTRATIVE_LABELS.ariaDivergent}
                        />
                      )}
                      {label}:
                    </p>
                    <div className="text-right shrink-0">
                      <p
                        data-testid={DEMONSTRATIVE_TEST_IDS.amount}
                        data-divergent={divergence.amount}
                        className={
                          divergence.amount
                            ? DEMONSTRATIVE_CLASSES.divergentAmount
                            : DEMONSTRATIVE_CLASSES.amount
                        }
                      >
                        {formatToBRL(movement.amount)}
                      </p>
                      <p
                        data-testid={DEMONSTRATIVE_TEST_IDS.quantity}
                        data-divergent={divergence.quantity}
                        className={
                          divergence.quantity
                            ? DEMONSTRATIVE_CLASSES.divergentQuantity
                            : DEMONSTRATIVE_CLASSES.quantity
                        }
                      >
                        {DEMONSTRATIVE_LABELS.quantityPrefix}{" "}
                        {movement.quantity}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            {group.listsTransfers && (
              <TransferList transfers={demonstrative.transfers ?? []} />
            )}
          </div>
        );
      })}
    </div>
  );
}
